/**
 * Utility service for working with user preferences
 */
class PreferencesService {
  static API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  /**
   * Get the current user preferences from the API
   * @param {string} token - Authentication token
   * @returns {Object} User preferences
   */
  static async getUserPreferences(token) {
    const defaultPreferences = {
      dayViewStartTime: '08:00',
      dayViewEndTime: '18:00',
      timezone: 'America/Mexico_City',
      treatmentPlanValidityDays: 30,
      calendarDotColor: '#3182ce'
    };

    try {
      const response = await fetch(`${this.API_BASE_URL}/preferences`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Error fetching user preferences:', response.status);
        return defaultPreferences;
      }

      const data = await response.json();
      return data.success ? { ...defaultPreferences, ...data.data } : defaultPreferences;
    } catch (error) {
      console.error('Error retrieving user preferences:', error);
      return defaultPreferences;
    }
  }

  /**
   * Get the day view time range as an object with start and end times
   * @param {Object} preferences - User preferences object
   * @returns {Object} Object containing startTime and endTime
   */
  static getDayViewTimeRange(preferences) {
    if (!preferences) {
      const defaultPreferences = this.getDefaultPreferences();
      return {
        startTime: defaultPreferences.dayViewStartTime,
        endTime: defaultPreferences.dayViewEndTime
      };
    }

    return {
      startTime: preferences.dayViewStartTime,
      endTime: preferences.dayViewEndTime
    };
  }

  /**
   * Get an array of hour slots for the day view based on user preferences
   * @param {Object} preferences - User preferences object
   * @returns {Array} Array of time slots in HH:MM format
   */
  static getDayViewTimeSlots(preferences) {
    const { startTime, endTime } = this.getDayViewTimeRange(preferences);
    const slots = [];

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    // Generate 30-minute slots
    for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const timeSlot = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
      slots.push(timeSlot);
    }

    return slots;
  }

  /**
   * Check if a given time is within the user's preferred day view range
   * @param {string} time - Time in HH:MM format
   * @param {Object} preferences - User preferences object
   * @returns {boolean} True if time is within range
   */
  static isTimeInDayViewRange(time, preferences) {
    const { startTime, endTime } = this.getDayViewTimeRange(preferences);
    return time >= startTime && time <= endTime;
  }

  /**
   * Get the duration of the day view in hours
   * @param {Object} preferences - User preferences object
   * @returns {number} Duration in hours
   */
  static getDayViewDuration(preferences) {
    const { startTime, endTime } = this.getDayViewTimeRange(preferences);

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    return (endMinutes - startMinutes) / 60;
  }

  /**
   * Save user preferences to the API
   * @param {Object} preferences - Preferences object to save
   * @param {string} token - Authentication token
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  static async saveUserPreferences(preferences, token) {
    try {
      const response = await fetch(`${this.API_BASE_URL}/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        console.error('Error saving user preferences:', response.status);
        return false;
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Error saving user preferences:', error);
      return false;
    }
  }

  /**
   * Get the user's selected timezone
   * @param {Object} preferences - User preferences object
   * @returns {string} Timezone identifier
   */
  static getUserTimezone(preferences) {
    if (!preferences) {
      const defaultPreferences = this.getDefaultPreferences();
      return defaultPreferences.timezone;
    }
    return preferences.timezone;
  }

  /**
   * Format a date using the user's timezone
   * @param {Date|string} date - Date to format
   * @param {Object} preferences - User preferences object
   * @param {Object} options - Intl.DateTimeFormat options
   * @returns {string} Formatted date string
   */
  static formatDateWithUserTimezone(date, preferences, options = {}) {
    const timezone = this.getUserTimezone(preferences);
    const dateObj = date instanceof Date ? date : new Date(date);

    return dateObj.toLocaleString('es-ES', {
      timeZone: timezone,
      ...options
    });
  }

  /**
   * Get default preferences
   * @returns {Object} Default preferences object
   */
  static getDefaultPreferences() {
    return {
      dayViewStartTime: '08:00',
      dayViewEndTime: '18:00',
      timezone: 'America/Mexico_City',
      calendarDotColor: '#3182ce'
    };
  }
}

export default PreferencesService;
